import React, { useRef, useEffect, useState } from 'react';
import SingleSelectDropdown from 'containers/NewCreativePreview/components/Filter Modal/singleSelectDropdown';
import MultiSelectDropdown from 'containers/NewCreativePreview/components/Filter Modal/multiSelectDropdown';
import {
  AssetDimensions,
  AssetType,
  Option,
} from 'containers/PodHomePage/components/Screen/components/OnDemandModal/types';
import * as S from '../../../../PodHomePage/components/Screen/components/OnDemandModal/style';
import { AssetUpload } from 'containers/PodHomePage/components/Screen/components/OnDemandModal/components/AssetUpload';
import { DestinationFields } from 'containers/PodHomePage/components/Screen/components/OnDemandModal/components/DestinationFields';
import { StreamMetadataFields } from 'containers/PodHomePage/components/Screen/components/OnDemandModal/components/StreamMetadataFields';
import { Button, ThemeProvider } from '@alphonsocode/kallos';
import { MdEdit } from 'react-icons/md';
import type { AdsureBatchItem } from '../hooks/useSaveWorkflow';
import type { StreamMetadataDetails } from 'containers/PodHomePage/components/Screen/components/OnDemandModal/types';

interface ManualUploadFormProps {
  primaryAsset: File | null;
  primaryPreview: string | null;
  primaryDimensions: AssetDimensions | null;
  secondaryAsset: File | null;
  secondaryPreview: string | null;
  secondaryDimensions: AssetDimensions | null;
  destinationAsset: File | null;
  destinationPreview: string | null;
  destinationDimensions: AssetDimensions | null;
  selectedAdType: Option | null;
  selectedDestinationType: Option | null;
  selectedAppId: Option | null;
  contentTargetValue: string;
  destinationURL: string;
  selectedAdslot: Option | null;
  selectedAdslots: Option[];
  selectedContentPartner: Option[];
  country: Option | null;
  osv: Option[] | null;
  adslotsData: any;
  isAdslotFetching: boolean;
  isAdslotLoading: boolean;
  loadAppOptions: (
    inputValue: string,
    callback: (options: Option[]) => void,
  ) => void;
  isAppFetching: boolean;
  isAppLoading: boolean;
  loadContentOptions: (
    inputValue: string,
    callback: (options: Option[]) => void,
  ) => void;
  isContentPartnerFetching: boolean;
  isContentPartnerLoading: boolean;
  onPrimaryFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    type: AssetType,
  ) => void;
  onPrimaryDrop: (e: React.DragEvent<HTMLDivElement>, type: AssetType) => void;
  onPrimaryDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onPrimaryRemove: () => void;
  onSecondaryFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    type: AssetType,
  ) => void;
  onSecondaryDrop: (
    e: React.DragEvent<HTMLDivElement>,
    type: AssetType,
  ) => void;
  onSecondaryDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onSecondaryRemove: () => void;
  onDestinationFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    type: AssetType,
  ) => void;
  onDestinationDrop: (
    e: React.DragEvent<HTMLDivElement>,
    type: AssetType,
  ) => void;
  onDestinationDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDestinationRemove: () => void;
  onAdTypeChange: (value: Option | null) => void;
  onDestinationTypeChange: (value: Option | null) => void;
  onCountryChange: (value: Option | null) => void;
  onOSVersionChange: (value: Option[]) => void;
  onAppIdChange: (value: Option | null) => void;
  onContentTargetChange: (value: string) => void;
  onDestinationURLChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAdslotChange: (value: Option | null) => void;
  onAdslotsChange: (value: Option[]) => void;
  onContentPartnerChange: (value: Option[]) => void;
  setIsOpen: (value: boolean) => void;
  isOpen: boolean;
  adslotValidation?: any;
  appNameOption: Option[];
  // When true, secondary was auto-populated from a video primary upload
  isVideoAutoFilled: boolean;
  batchItems: AdsureBatchItem[];
  selectedBatchItemId: string | null;
  primaryAssetError: string;
  uploadActionError: string;
  canAddCurrentToBatch: boolean;
  isEditingBatchItem: boolean;
  allowMultiplePrimaryUpload: boolean;
  onAddCurrentToBatch: () => void;
  onSelectBatchItem: (clientId: string) => void;
  onRemoveBatchItem: (clientId: string) => void;
  onAddMoreCreatives: () => void;
  clickableStreamUrl: string;
  clickableStreamMetadata: StreamMetadataDetails | null;
  clickableStreamError: string;
  clickableStreamLoading: boolean;
  onClickableStreamUrlChange: (value: string) => void;
  onClickableStreamUrlBlur: () => void;
  onClickableStreamDurationChange: (value: string) => void;
  optionalStreamUrl: string;
  optionalStreamMetadata: StreamMetadataDetails | null;
  optionalStreamError: string;
  optionalStreamLoading: boolean;
  onOptionalStreamUrlChange: (value: string) => void;
  onOptionalStreamUrlBlur: () => void;
  onOptionalStreamDurationChange: (value: string) => void;
  destinationStreamUrl: string;
  destinationStreamMetadata: StreamMetadataDetails | null;
  destinationStreamError: string;
  destinationStreamLoading: boolean;
  onDestinationStreamUrlChange: (value: string) => void;
  onDestinationStreamUrlBlur: () => void;
  onDestinationStreamDurationChange: (value: string) => void;
}

export const ManualUploadFormAdsure: React.FC<ManualUploadFormProps> = ({
  primaryAsset,
  primaryPreview,
  primaryDimensions,
  secondaryAsset,
  secondaryPreview,
  secondaryDimensions,
  destinationAsset,
  destinationPreview,
  destinationDimensions,
  selectedAdType,
  selectedDestinationType,
  selectedAppId,
  contentTargetValue,
  destinationURL,
  selectedAdslot,
  selectedAdslots,
  selectedContentPartner,
  country,
  osv,
  adslotsData,
  isAdslotFetching,
  isAdslotLoading,
  loadAppOptions,
  isAppFetching,
  isAppLoading,
  loadContentOptions,
  isContentPartnerFetching,
  isContentPartnerLoading,
  onPrimaryFileUpload,
  onPrimaryDrop,
  onPrimaryDragOver,
  onPrimaryRemove,
  onSecondaryFileUpload,
  onSecondaryDrop,
  onSecondaryDragOver,
  onSecondaryRemove,
  onDestinationFileUpload,
  onDestinationDrop,
  onDestinationDragOver,
  onDestinationRemove,
  onAdTypeChange,
  onDestinationTypeChange,
  onAppIdChange,
  onContentTargetChange,
  onDestinationURLChange,
  onAdslotsChange,
  onContentPartnerChange,
  setIsOpen,
  isOpen,
  adslotValidation,
  onCountryChange,
  onOSVersionChange,
  appNameOption,
  isVideoAutoFilled,
  batchItems,
  selectedBatchItemId,
  primaryAssetError,
  uploadActionError,
  canAddCurrentToBatch,
  isEditingBatchItem,
  allowMultiplePrimaryUpload,
  onAddCurrentToBatch,
  onSelectBatchItem,
  onRemoveBatchItem,
  onAddMoreCreatives,
  clickableStreamUrl,
  clickableStreamMetadata,
  clickableStreamError,
  clickableStreamLoading,
  onClickableStreamUrlChange,
  onClickableStreamUrlBlur,
  onClickableStreamDurationChange,
  optionalStreamUrl,
  optionalStreamMetadata,
  optionalStreamError,
  optionalStreamLoading,
  onOptionalStreamUrlChange,
  onOptionalStreamUrlBlur,
  onOptionalStreamDurationChange,
  destinationStreamUrl,
  destinationStreamMetadata,
  destinationStreamError,
  destinationStreamLoading,
  onDestinationStreamUrlChange,
  onDestinationStreamUrlBlur,
  onDestinationStreamDurationChange,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const assetUploadRef = useRef<HTMLDivElement>(null);
  const [isAddMoreHovered, setIsAddMoreHovered] = useState(false);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isOpen]);

  const isExpandableBanner = selectedAdType?.value === 'expandableBannerTo';
  const isClickableStream = selectedAdType?.value === 'clickableStreamTo';
  const requiresSecondaryUpload =
    selectedAdType?.value === 'clickableVideoTo' ||
    selectedAdType?.value === 'companionTo';
  const showSecondaryUpload = requiresSecondaryUpload || isExpandableBanner;
  const selectedSlotNames = selectedAdslots.map(
    adslot => adslot.value || adslot.label,
  );
  const toOptions = (items: string[] = []) =>
    items.map(item => ({ key: item, value: item, label: item }));
  const getCommonOptionsForSlots = (slotNames: string[], field: string) => {
    if (!slotNames.length) return [];
    const optionSets = slotNames.map(
      slotName => adslotValidation?.validateCheck?.[slotName]?.[field] || [],
    );
    const [firstSet = []] = optionSets;
    return firstSet.filter((item: string) =>
      optionSets.every(options => options.includes(item)),
    );
  };
  const getCommonOptions = (field: string) =>
    getCommonOptionsForSlots(selectedSlotNames, field);
  const hasCompatibleTargetingFields = (slotNames: string[]) =>
    ['adType', 'destinationType', 'countryCode', 'osvs'].every(
      field => getCommonOptionsForSlots(slotNames, field).length > 0,
    );
  const canAddAdslotToSelection = (slotName: string) => {
    if (!slotName) return false;
    if (selectedSlotNames.includes(slotName)) return true;
    const nextSlotNames = [...selectedSlotNames, slotName];
    return hasCompatibleTargetingFields(nextSlotNames);
  };
  const getCompatibleAdslotOptions = () =>
    toOptions(adslotValidation.adslots || []).filter(option =>
      canAddAdslotToSelection(option.value),
    );
  const handleCompatibleAdslotsChange = (nextAdslots: Option[] = []) => {
    const compatibleSelection = nextAdslots.reduce(
      (acc: Option[], adslot) => {
        const slotName = adslot.value || adslot.label;
        const nextSlotNames = [
          ...acc.map(item => item.value || item.label),
          slotName,
        ];
        if (hasCompatibleTargetingFields(nextSlotNames)) {
          acc.push(adslot);
        }
        return acc;
      },
      [],
    );

    onAdslotsChange(compatibleSelection);
  };
  const adTypeOptions = getCommonOptions('adType');
  const destinationTypeOptions = getCommonOptions('destinationType');
  const countryOptions = getCommonOptions('countryCode');
  const osvOptions = getCommonOptions('osvs');
  const hasScreensaverSlot = selectedAdslots.some(
    adslot => adslot.label?.toLowerCase() === 'portal/screensaver@main',
  );
  const handleAddMoreCreativesClick = () => {
    onAddMoreCreatives();
    window.setTimeout(() => {
      assetUploadRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };
  const handleSelectBatchItem = (clientId: string) => {
    onSelectBatchItem(clientId);
    window.setTimeout(() => {
      assetUploadRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };

  useEffect(() => {
    if (!selectedSlotNames.length) return;

    if (
      selectedAdType &&
      !adTypeOptions.includes(selectedAdType.value || selectedAdType.label)
    ) {
      onAdTypeChange(null);
    }

    if (
      selectedDestinationType &&
      !destinationTypeOptions.includes(
        selectedDestinationType.value || selectedDestinationType.label,
      )
    ) {
      onDestinationTypeChange(null);
    }

    if (country && !countryOptions.includes(country.value || country.label)) {
      onCountryChange(null);
    }

    if (osv && osv.length > 0) {
      const validOsv = osv.filter(option =>
        osvOptions.includes(option.value || option.label),
      );
      if (validOsv.length !== osv.length) {
        onOSVersionChange(validOsv);
      }
    }
  }, [
    adTypeOptions,
    country,
    countryOptions,
    destinationTypeOptions,
    onAdTypeChange,
    onCountryChange,
    onDestinationTypeChange,
    onOSVersionChange,
    osv,
    osvOptions,
    selectedAdType,
    selectedDestinationType,
    selectedSlotNames,
  ]);

  return (
    <S.UploadSection ref={scrollRef}>
      <S.AssetLabel>Asset Inputs Needed</S.AssetLabel>
      <S.SubText>Drag files or browse</S.SubText>

      {batchItems.length > 0 && (
        <S.FormSection
          style={{
            backgroundColor: '#f8fbfc',
            border: '1px solid #d7e3e5',
            borderRadius: 8,
            marginTop: 8,
            marginBottom: 16,
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: 12,
              marginBottom: 10,
            }}
          >
            <S.AssetLabel>Batch Queue</S.AssetLabel>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {batchItems.map((item, index) => {
              const isSelected = selectedBatchItemId === item.clientId;

              return (
                <S.FormRow
                  key={item.clientId}
                  onClick={() => handleSelectBatchItem(item.clientId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSelectBatchItem(item.clientId);
                    }
                  }}
                  style={{
                    alignItems: 'center',
                    backgroundColor: isSelected ? '#eef7f8' : '#ffffff',
                    border: isSelected
                      ? '1px solid #94c8d0'
                      : '1px solid #d7e3e5',
                    borderRadius: 8,
                    boxShadow: isSelected
                      ? '0 1px 4px rgba(0, 110, 125, 0.12)'
                      : 'none',
                    cursor: 'pointer',
                    display: 'grid',
                    gap: 12,
                    gridTemplateColumns:
                      'minmax(0, 1.4fr) minmax(170px, 0.9fr) auto',
                    margin: 0,
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 8,
                        minWidth: 0,
                      }}
                    >
                      <S.Label
                        style={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {index + 1}. {item.label}
                      </S.Label>
                      <span
                        style={{
                          alignItems: 'center',
                          color: isSelected ? '#006e7d' : '#137f95',
                          display: 'inline-flex',
                          flexShrink: 0,
                          fontSize: 12,
                          fontWeight: 700,
                          gap: 4,
                        }}
                      >
                        <MdEdit size={14} aria-hidden />
                        {isSelected ? 'Editing' : 'Edit'}
                      </span>
                    </div>
                    <S.SubText>
                      {item.selectedAdslots.length} ad slot
                      {item.selectedAdslots.length === 1 ? '' : 's'} /{' '}
                      {item.osv.length} OS target
                      {item.osv.length === 1 ? '' : 's'}
                    </S.SubText>
                  </div>
                  <div
                    style={{
                      color: item.isValid ? '#137f95' : '#b70303',
                      fontSize: 12,
                      fontWeight: 700,
                      justifySelf: 'center',
                      textAlign: 'center',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.isValid
                      ? 'Ready'
                      : 'Invalid test: please correct configuration'}
                  </div>
                  <ThemeProvider defaultMode="light">
                    <Button
                      colorVariant="deepGreen"
                      renderVariant="borderless"
                      variant="borderless"
                      onClick={event => {
                        event.stopPropagation();
                        onRemoveBatchItem(item.clientId);
                      }}
                    >
                      Remove
                    </Button>
                  </ThemeProvider>
                </S.FormRow>
              );
            })}
          </div>
          <div
            style={{
              borderTop: '1px solid #d7e3e5',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 12,
              paddingTop: 12,
            }}
          >
            <ThemeProvider defaultMode="light">
              <Button
                colorVariant="deepGreen"
                renderVariant="outlined"
                variant="outlined"
                onClick={handleAddMoreCreativesClick}
                onMouseEnter={() => setIsAddMoreHovered(true)}
                onMouseLeave={() => setIsAddMoreHovered(false)}
                style={{
                  backgroundColor: isAddMoreHovered ? '#ffffff' : '#137f95',
                  borderColor: '#137f95',
                  color: isAddMoreHovered ? '#137f95' : '#ffffff',
                  transition:
                    'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
                }}
              >
                Add More Creatives
              </Button>
            </ThemeProvider>
          </div>
        </S.FormSection>
      )}

      {/* Primary asset — accepts both images and videos */}
      <div
        ref={assetUploadRef}
        style={{
          borderTop: batchItems.length > 0 ? '1px solid #d7e3e5' : undefined,
          marginTop: batchItems.length > 0 ? 16 : 0,
          paddingTop: batchItems.length > 0 ? 16 : 0,
          scrollMarginTop: 12,
        }}
      >
        <AssetUpload
          type="primary"
          file={primaryAsset}
          preview={primaryPreview}
          dimensions={primaryDimensions}
          label="Primary Asset"
          accept="image/*,video/mp4,video/webm,video/quicktime"
          onFileUpload={onPrimaryFileUpload}
          onDrop={onPrimaryDrop}
          onDragOver={onPrimaryDragOver}
          onRemove={onPrimaryRemove}
          required
          multiple={allowMultiplePrimaryUpload}
        />
      </div>

      {uploadActionError && (
        <S.FormRow style={{ marginTop: '6px' }}>
          <S.FormGroup>
            <span
              style={{
                color: 'red',
                fontSize: 12,
                marginBottom: 8,
                fontWeight: 700,
              }}
            >
              {uploadActionError}
            </span>
          </S.FormGroup>
        </S.FormRow>
      )}

      {primaryAssetError && (
        <S.FormRow style={{ marginTop: '6px' }}>
          <S.FormGroup>
            <span
              style={{
                color: 'red',
                fontSize: 12,
                marginBottom: 8,
                fontWeight: 700,
              }}
            >
              {primaryAssetError}
            </span>
          </S.FormGroup>
        </S.FormRow>
      )}

      {/* Informational banner when dummy was auto-generated */}
      {isVideoAutoFilled && (
        <S.FormRow style={{ marginTop: '6px' }}>
          <S.FormGroup>
            <span
              style={{
                color: '#555',
                fontSize: 12,
                marginBottom: 4,
                fontStyle: 'italic',
              }}
            >
              A banner has been auto-generated as primary asset. Your video has
              been attached as the secondary asset and Ad Type has been set to{' '}
              <strong>clickableVideoTo</strong>.
            </span>
          </S.FormGroup>
        </S.FormRow>
      )}

      {adslotValidation.validateError !== '' && (
        <S.FormRow style={{ marginTop: '6px' }}>
          <S.FormGroup>
            <span
              style={{
                color: 'red',
                fontSize: 12,
                marginBottom: 8,
                fontWeight: 700,
              }}
            >
              {adslotValidation.validateError}
            </span>
          </S.FormGroup>
        </S.FormRow>
      )}

      {primaryAsset && adslotValidation.validationStatus && (
        <S.FormRow>
          <S.FormGroup>
            <S.Label style={{ marginTop: '5px' }}>Ad Slot</S.Label>
            <div style={{ fontSize: 13, width: 500 }}>
              <MultiSelectDropdown
                placeholder="Select Ad Slots..."
                value={selectedAdslots}
                onChange={handleCompatibleAdslotsChange}
                loadOptions={inputValue => {
                  const allOptions = getCompatibleAdslotOptions();
                  if (!inputValue) return Promise.resolve(allOptions);
                  const filtered = allOptions.filter((opt: Option) =>
                    opt.label.toLowerCase().includes(inputValue.toLowerCase()),
                  );
                  return Promise.resolve(filtered);
                }}
                defaultOptions={getCompatibleAdslotOptions()}
                disabled={false}
                isLoading={isAdslotFetching || isAdslotLoading}
                setIsOpen={setIsOpen}
              />
            </div>
          </S.FormGroup>
        </S.FormRow>
      )}

      {primaryAsset &&
        selectedAdslots.length > 0 &&
        adslotValidation.validationStatus && (
          <S.FormSection ref={scrollRef}>
            <S.FormRow>
              <S.FormGroup>
                <S.Label>Ad type</S.Label>
                <div style={{ fontSize: 13 }}>
                  <SingleSelectDropdown
                    placeholder="Select..."
                    value={selectedAdType}
                    onChange={onAdTypeChange}
                    loadOptions={inputValue => {
                      if (!inputValue)
                        return Promise.resolve(toOptions(adTypeOptions));
                      const filtered = toOptions(adTypeOptions).filter(item =>
                        item.label
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()),
                      );
                      return Promise.resolve(filtered);
                    }}
                    defaultOptions={toOptions(adTypeOptions)}
                    isLoading={false}
                    setIsOpen={setIsOpen}
                  />
                </div>
              </S.FormGroup>

              <S.FormGroup>
                <S.Label>Destination type</S.Label>
                <div style={{ fontSize: 13 }}>
                  <SingleSelectDropdown
                    placeholder="Select..."
                    value={selectedDestinationType}
                    onChange={onDestinationTypeChange}
                    loadOptions={inputValue => {
                      if (!inputValue)
                        return Promise.resolve(
                          toOptions(destinationTypeOptions),
                        );
                      const filtered = toOptions(destinationTypeOptions).filter(
                        item =>
                          item.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase()),
                      );
                      return Promise.resolve(filtered);
                    }}
                    defaultOptions={toOptions(destinationTypeOptions)}
                    isLoading={false}
                    setIsOpen={setIsOpen}
                  />
                </div>
              </S.FormGroup>

              <S.FormGroup>
                <S.Label>Country</S.Label>
                <div style={{ fontSize: 13 }}>
                  <SingleSelectDropdown
                    placeholder="Select..."
                    value={country}
                    onChange={onCountryChange}
                    loadOptions={inputValue => {
                      if (!inputValue)
                        return Promise.resolve(toOptions(countryOptions));
                      const filtered = toOptions(countryOptions).filter(item =>
                        item.label
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()),
                      );
                      return Promise.resolve(filtered);
                    }}
                    defaultOptions={toOptions(countryOptions)}
                    isLoading={false}
                    setIsOpen={setIsOpen}
                  />
                </div>
              </S.FormGroup>

              <S.FormGroup>
                <S.Label>OS</S.Label>
                <div style={{ fontSize: 13 }}>
                  <MultiSelectDropdown
                    placeholder="Select..."
                    value={osv}
                    onChange={onOSVersionChange}
                    loadOptions={inputValue => {
                      if (!inputValue)
                        return Promise.resolve(toOptions(osvOptions));
                      const filtered = toOptions(osvOptions).filter(item =>
                        item.label
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()),
                      );
                      return Promise.resolve(filtered);
                    }}
                    defaultOptions={toOptions(osvOptions)}
                    isLoading={false}
                    setIsOpen={setIsOpen}
                    disabled={false}
                  />
                </div>
              </S.FormGroup>
            </S.FormRow>
            {selectedDestinationType && (
              <DestinationFields
                destinationType={selectedDestinationType.value as any}
                selectedAppId={selectedAppId}
                contentTargetValue={contentTargetValue}
                destinationURL={destinationURL}
                destinationAsset={destinationAsset}
                destinationPreview={destinationPreview}
                destinationDimensions={destinationDimensions}
                loadAppOptions={loadAppOptions}
                isAppFetching={isAppFetching}
                isAppLoading={isAppLoading}
                onAppIdChange={onAppIdChange}
                onContentTargetChange={onContentTargetChange}
                onDestinationURLChange={onDestinationURLChange}
                onDestinationFileUpload={onDestinationFileUpload}
                onDestinationDrop={onDestinationDrop}
                onDestinationDragOver={onDestinationDragOver}
                onDestinationRemove={onDestinationRemove}
                setIsOpen={setIsOpen}
                appNameOption={appNameOption}
                streamMetadata={destinationStreamMetadata}
                streamMetadataError={destinationStreamError}
                streamMetadataLoading={destinationStreamLoading}
                streamUrl={destinationStreamUrl}
                onStreamDurationChange={onDestinationStreamDurationChange}
                onStreamUrlBlur={onDestinationStreamUrlBlur}
                onStreamUrlChange={onDestinationStreamUrlChange}
              />
            )}

            {isClickableStream && (
              <StreamMetadataFields
                title="Clickable Stream"
                details={clickableStreamMetadata}
                error={clickableStreamError}
                isLoading={clickableStreamLoading}
                onDurationChange={onClickableStreamDurationChange}
                onUrlBlur={onClickableStreamUrlBlur}
                onUrlChange={onClickableStreamUrlChange}
                urlValue={clickableStreamUrl}
              />
            )}

            {showSecondaryUpload && (
              <S.FormRow>
                <S.FormGroup>
                  <S.AssetLabel>Secondary Video Asset</S.AssetLabel>
                  <S.SubText>
                    {isVideoAutoFilled
                      ? 'Auto-filled from your video upload — remove primary to change'
                      : isExpandableBanner
                        ? 'Optional seamless video'
                        : 'Drag files or browse'}
                  </S.SubText>
                  <AssetUpload
                    type="secondary"
                    file={secondaryAsset}
                    preview={secondaryPreview}
                    dimensions={secondaryDimensions}
                    label="Secondary Video Asset"
                    accept="video/*"
                    onFileUpload={
                      isVideoAutoFilled ? () => {} : onSecondaryFileUpload
                    }
                    onDrop={isVideoAutoFilled ? () => {} : onSecondaryDrop}
                    onDragOver={
                      isVideoAutoFilled ? () => {} : onSecondaryDragOver
                    }
                    onRemove={isVideoAutoFilled ? () => {} : onSecondaryRemove}
                    required={requiresSecondaryUpload}
                    disabled={isVideoAutoFilled}
                  />
                </S.FormGroup>
              </S.FormRow>
            )}

            {isExpandableBanner && (
              <StreamMetadataFields
                title="Optional Hover Stream"
                details={optionalStreamMetadata}
                error={optionalStreamError}
                isLoading={optionalStreamLoading}
                isOptional
                onDurationChange={onOptionalStreamDurationChange}
                onUrlBlur={onOptionalStreamUrlBlur}
                onUrlChange={onOptionalStreamUrlChange}
                urlValue={optionalStreamUrl}
              />
            )}

            <S.FormRow>
              {hasScreensaverSlot && (
                <S.FormGroup>
                  <S.Label>Content Partners</S.Label>
                  <div style={{ fontSize: 13, width: 400 }}>
                    <MultiSelectDropdown
                      placeholder="Type to search content partners..."
                      value={selectedContentPartner}
                      onChange={onContentPartnerChange}
                      loadOptions={loadContentOptions}
                      defaultOptions={[]}
                      disabled={false}
                      isLoading={
                        isContentPartnerFetching || isContentPartnerLoading
                      }
                      setIsOpen={setIsOpen}
                    />
                  </div>
                </S.FormGroup>
              )}
            </S.FormRow>

            {!isEditingBatchItem && (
              <S.FormRow style={{ justifyContent: 'flex-end' }}>
                <ThemeProvider defaultMode="light">
                  <Button
                    colorVariant="deepGreen"
                    renderVariant="outlined"
                    onClick={onAddCurrentToBatch}
                    disabled={!canAddCurrentToBatch}
                  >
                    Add Creative to Batch
                  </Button>
                </ThemeProvider>
              </S.FormRow>
            )}
          </S.FormSection>
        )}
    </S.UploadSection>
  );
};
